import { FormEvent, useState } from 'react';

import { Button } from '../shared/Button';
import { Modal } from '../shared';

import { useActivations, useEvents } from '../hooks/events';

import { handleError } from '../utils/utils';

import styles from './Events.module.scss';
import {
    formatVersionRange,
    hasVersionBounds,
    resolveEffectiveVersionBounds,
} from './versionRange';

export function ActivationsView() {
    const { data: events = [] } = useEvents();
    const {
        data: activations = [],
        createActivation,
        deleteActivation,
    } = useActivations();

    const [newActivationKey, setNewActivationKey] = useState('');
    const [newActivationEventId, setNewActivationEventId] = useState('');
    const [newActivationPayload, setNewActivationPayload] = useState('');
    const [newMinimumVersion, setNewMinimumVersion] = useState('');
    const [newMaximumVersion, setNewMaximumVersion] = useState('');

    const selectedEvent = events.find(
        (event) => event.id === newActivationEventId,
    );
    const minimumPlaceholder = selectedEvent?.minimumVersion
        ? `Inherits ${selectedEvent.minimumVersion} from event (optional override)`
        : 'Min client version, inclusive (optional, e.g. 1.40.0)';
    const maximumPlaceholder = selectedEvent?.maximumVersion
        ? `Inherits ${selectedEvent.maximumVersion} from event (optional override)`
        : 'Max client version, exclusive (optional, e.g. 1.40.0)';

    async function onCreateActivation(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if (!newActivationKey.trim() || !newActivationEventId) return;

        let parsedPayload: Record<string, unknown> | null = null;
        if (newActivationPayload.trim()) {
            try {
                parsedPayload = JSON.parse(newActivationPayload) as Record<
                    string,
                    unknown
                >;
            } catch (err) {
                handleError('Payload must be valid JSON', err, false);
                return;
            }
        }

        try {
            await createActivation.mutateAsync({
                activationKey: newActivationKey.trim(),
                eventId: newActivationEventId,
                payload: parsedPayload,
                minimumVersion: newMinimumVersion.trim() || undefined,
                maximumVersion: newMaximumVersion.trim() || undefined,
            });
            setNewActivationKey('');
            setNewActivationPayload('');
            setNewMinimumVersion('');
            setNewMaximumVersion('');
        } catch (err) {
            handleError('Failed to create activation', err);
        }
    }

    async function onDeleteActivation(activationId: string) {
        const isConfirmed = await Modal.confirm({
            heading: 'Delete activation',
            text: 'Really delete this activation?',
            okLabel: 'Yes do it!',
        });
        if (!isConfirmed) return;

        try {
            await deleteActivation.mutateAsync(activationId);
        } catch (err) {
            handleError('Failed to delete activation', err);
        }
    }

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2>Activations library</h2>
                <p>
                    Create activation keys and payloads before scheduling them.
                    Version bounds are half-open: the max is the first version
                    that no longer gets the activation, so “below 1.40.0” and
                    “1.40.0 and up” fit together exactly.
                </p>
            </div>

            <form className={styles.eventsForm} onSubmit={onCreateActivation}>
                <div className={styles.formRowCompact}>
                    <input
                        className={styles.input}
                        placeholder="Activation key (example: double-xp)"
                        value={newActivationKey}
                        onChange={(e) => setNewActivationKey(e.target.value)}
                    />
                    <select
                        className={styles.select}
                        value={newActivationEventId}
                        onChange={(e) =>
                            setNewActivationEventId(e.target.value)
                        }
                    >
                        <option value="">Select event</option>
                        {events.map((event) => (
                            <option key={event.id} value={event.id}>
                                {event.eventKey}
                            </option>
                        ))}
                    </select>
                    <div className={styles.formButtonWrap}>
                        <Button type="submit" color="primary">
                            Create activation
                        </Button>
                    </div>
                </div>
                <div className={styles.formRowCompact}>
                    <input
                        className={styles.input}
                        placeholder={minimumPlaceholder}
                        value={newMinimumVersion}
                        onChange={(e) => setNewMinimumVersion(e.target.value)}
                    />
                    <input
                        className={styles.input}
                        placeholder={maximumPlaceholder}
                        value={newMaximumVersion}
                        onChange={(e) => setNewMaximumVersion(e.target.value)}
                    />
                </div>
                <textarea
                    className={`${styles.textarea} ${styles.payloadTextarea}`}
                    placeholder='Payload JSON (optional), example: {"multiplier":2}'
                    value={newActivationPayload}
                    onChange={(e) => setNewActivationPayload(e.target.value)}
                />
            </form>

            <table className={styles.table}>
                <thead>
                    <tr>
                        <th>Activation key</th>
                        <th>Event</th>
                        <th>Client version</th>
                        <th>Payload</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {activations.map((activation) => {
                        const event = events.find(
                            (item) => item.id === activation.eventId,
                        );
                        const effective = resolveEffectiveVersionBounds(
                            event ?? {},
                            activation,
                        );
                        const isInherited =
                            !hasVersionBounds(activation) &&
                            hasVersionBounds(effective);
                        return (
                            <tr key={activation.id}>
                                <td>{activation.activationKey}</td>
                                <td>{event?.eventKey || activation.eventId}</td>
                                <td>
                                    <span
                                        className={
                                            hasVersionBounds(effective)
                                                ? styles.versionBadgeBounded
                                                : styles.versionBadge
                                        }
                                        title={
                                            isInherited
                                                ? 'Inherited from the event; this activation sets no override'
                                                : undefined
                                        }
                                    >
                                        {formatVersionRange(
                                            effective.minimumVersion,
                                            effective.maximumVersion,
                                        )}
                                        {isInherited && ' (inherited)'}
                                    </span>
                                </td>
                                <td>
                                    <pre className={styles.payloadPreview}>
                                        {activation.payload
                                            ? JSON.stringify(
                                                  activation.payload,
                                                  null,
                                                  2,
                                              )
                                            : '-'}
                                    </pre>
                                </td>
                                <td>
                                    <Button
                                        type="button"
                                        color="danger"
                                        size="small"
                                        onClick={() =>
                                            void onDeleteActivation(
                                                activation.id,
                                            )
                                        }
                                    >
                                        Delete
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
